import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RoomEmote } from "@uno/protocol";
import { emoteEventBus, type EmoteEvent } from "hook/room/useRoomEmoteListener";

type RoomPlayerEmoteProps = {
    playerId: string;
};

const RoomPlayerEmote = ({ playerId }: RoomPlayerEmoteProps) => {
    const [emote, setEmote] = React.useState<string | null>(null);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        const handler = (e: Event) => {
            const { playerId: emotePlayerId, emoteId } = (e as CustomEvent<EmoteEvent>).detail;

            if(emotePlayerId !== playerId) {
                return;
            }

            if(timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setEmote(RoomEmote[emoteId] || null);

            timeoutRef.current = setTimeout(() => {
                setEmote(null);
                timeoutRef.current = null;
            }, 2500);
        };

        emoteEventBus.addEventListener("emote", handler);

        return () => {
            emoteEventBus.removeEventListener("emote", handler);

            if(timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [playerId]);

    return (
        <AnimatePresence>
            {emote && (
                <motion.div
                    key={emote}
                    initial={{ scale: 0, opacity: 0, y: 0 }}
                    animate={{ scale: [0, 1.3, 1], opacity: 1, y: 0 }}
                    exit={{ scale: 0.5, opacity: 0, y: -40 }}
                    transition={{ duration: 0.3, exit: { duration: 0.6 } }}
                    className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center"
                >
                    <span className="text-5xl drop-shadow-lg">{emote}</span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default RoomPlayerEmote;
